import React, { useEffect, useState } from 'react';
import { X, FileText, Plus, Loader2, AlertCircle } from 'lucide-react';
import reportConfigurationService, {
  StandardReportInfo,
  ReportTypeLabels,
  ReportType
} from '../../../../services/reportConfigurationService';

interface TemplatePickerDialogProps {
  isOpen: boolean;
  companyNo: string;
  onCancel: () => void;
  onPick: (template: StandardReportInfo | null) => void;
}

export const TemplatePickerDialog: React.FC<TemplatePickerDialogProps> = ({
  isOpen,
  companyNo,
  onCancel,
  onPick
}) => {
  const [templates, setTemplates] = useState<StandardReportInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    reportConfigurationService
      .getStandardReportCatalog(companyNo)
      .then((list) => {
        if (cancelled) return;
        setTemplates(list);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Kunne ikke hente maler');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, companyNo]);

  if (!isOpen) return null;

  const picked = templates.find((t) => t.reportName === selected) || null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Start ny rapport</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" type="button">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Velg en standardmal som utgangspunkt — dimensjoner og rapporttype blir forhåndsutfylt.
            Malen selv endres ikke; det opprettes alltid en ny rapport.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Mal</label>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500 p-3 bg-gray-50 rounded">
                <Loader2 size={14} className="animate-spin" />
                Laster maler…
              </div>
            ) : (
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">— Ingen mal (start blank) —</option>
                {templates.map((t) => (
                  <option key={t.reportName} value={t.reportName}>
                    {t.displayName} ({t.paperWidth}×{t.paperHeight}mm
                    {t.landscape ? ', liggende' : ''})
                  </option>
                ))}
              </select>
            )}
          </div>

          {picked && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm space-y-1">
              <div className="flex items-center gap-2 font-medium text-blue-900">
                <FileText size={14} />
                {picked.displayName}
              </div>
              {picked.description && (
                <div className="text-blue-800">{picked.description}</div>
              )}
              <div className="text-xs text-blue-700 pt-1">
                Type: {ReportTypeLabels[picked.reportType as ReportType] || picked.reportType} · {picked.paperWidth}×{picked.paperHeight}mm{picked.landscape ? ' · liggende' : ''}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
          >
            Avbryt
          </button>
          <button
            type="button"
            onClick={() => onPick(picked)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            <Plus size={16} />
            {picked ? 'Bruk mal og opprett' : 'Start blank'}
          </button>
        </div>
      </div>
    </div>
  );
};
